import { createSignal, Show } from "solid-js";
import { Text, View, hexColor } from "@lightningtv/solid";
import PinKeyboard from "../../../components/PinKeyboard";

// Data-driven steps — the title and the step order itself both come from
// this list, instead of a hand-written ternary chain per label.
const CHANGE_PIN_STEPS = [
  { key: "verifyOld", title: "Enter Current PIN" },
  { key: "enterNew", title: "Enter New PIN" },
  { key: "confirmNew", title: "Confirm New PIN" },
] as const;

type ChangePinProps = {
  onPinSet: () => void;
  keyboardRef?: any;
  // Where Left on the keyboard's first key sends focus back to (the
  // "Change PIN" menu item in SettingsPanel).
  onBackLeft?: () => void;
};

const ChangePin = (props: ChangePinProps) => {
  const [step, setStep] = createSignal<"verifyOld" | "enterNew" | "confirmNew" | "success">("verifyOld");
  const [tempNewPin, setTempNewPin] = createSignal("");
  const [errorMessage, setErrorMessage] = createSignal("");

  const keyboardRef = props.keyboardRef;
  const stepIndex = () => CHANGE_PIN_STEPS.findIndex((s) => s.key === step());

  const handlePinSubmit = (pin: string) => {
    const savedPin = localStorage.getItem("userPin");
    setErrorMessage("");

    if (step() === "verifyOld") {
      if (pin === savedPin) {
        setStep("enterNew");
      } else {
        setErrorMessage("That PIN doesn't match. Try again.");
      }
    } else if (step() === "enterNew") {
      setTempNewPin(pin);
      setStep("confirmNew");
    } else if (step() === "confirmNew") {
      if (pin === tempNewPin()) {
        localStorage.setItem("userPin", pin);
        setStep("success");

        setTimeout(() => {
          props.onPinSet();
          setStep("verifyOld");
          setTempNewPin("");
        }, 2000);
      } else {
        setErrorMessage("PINs didn't match. Enter the new PIN again.");
        setStep("enterNew");
        setTempNewPin("");
      }
    }

    keyboardRef?.resetInput?.();
  };

  return (
    <View>
      <View x={610}>
        <Text x={190} y={80} fontSize={36}>
          {step() === "success" ? "PIN Changed Successfully!" : CHANGE_PIN_STEPS[stepIndex()].title}
        </Text>

        <Show when={errorMessage()}>
          <View x={190} y={130} width={600} height={50} style={{ borderRadius: 10, color: hexColor("#ff000033") }}>
            <Text x={16} y={12} fontSize={22} color={hexColor("#ff6b6b")}>
              {errorMessage()}
            </Text>
          </View>
        </Show>

        {step() === "success" ? (
          <View y={150}>
            <Text x={120} y={50} fontSize={28} color={hexColor("#00ff00")}>
              Your PIN has been changed successfully!
            </Text>
          </View>
        ) : (
          <PinKeyboard
            onSubmit={handlePinSubmit}
            keyboardRef={keyboardRef}
            autoFocus={false}
            onBackLeft={() => props.onBackLeft?.()}
          />
        )}
      </View>
    </View>
  );
};

export default ChangePin;
